import { useState } from "react";
import { Route, Routes, useNavigate, useLocation, useSearchParams, Navigate, useParams } from "react-router-dom";
import { DataContainerContext } from "./context/DataContainerContext";
import "./assets/scss/styles.scss";
// Pages
import { TruckSelector } from "./pages/TruckSelector/TruckSelector";
import { Configurator } from "./pages/Configurator/Configurator";
import Markets from "./pages/Markets/markets";
import Summary from "./pages/Summary/Summary";
import { useContext, useEffect } from "react";
import DataContainer from "./services/DataContainer";
import { getLocales } from "./services/ApiService";
import { MarketSettings } from "./enum/enums";
import { ArToWeb } from "./pages/ArToWeb/ArToWeb";
import { AdobeEventActions } from "./enum/adobe/adobeEventActions";
import { StepNumbers } from "./enum/adobe/stepNumbers";
import { AdobeEventType } from "./enum/adobe/adobeEventType";
import { StepNames } from "./enum/adobe/stepNames";
import { SubStepNames } from "./enum/adobe/subStepNames";

function ConfiguratorWithoutModelRedirect() {
  const { locale } = useParams();
  return <Navigate to={`/${locale}`} replace />;
}

function ConfiguratorRoute() {
  const { modelName } = useParams();
  return <Configurator key={modelName} />;
}

const isLocaleExisting = (locales: { locale: string }[], locale: string) => {
  return !!locales?.find((loc) => loc.locale === locale);
};

function App() {
  const {
    setCurrentLocale,
    setSearchParams,
    currentLocale,
    setDefaultVariantColor,
    setIsColorComingFromURL } = useContext(DataContainerContext);
  const navigate = useNavigate();
  const startTime = +new Date();
  const currentPath = useLocation();
  const [ searchParams ]: any = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const endTime = +new Date();
  const pathname = currentPath.pathname.replace("/", "");
  const [availableLocales, setAvailableLocales] = useState([]);

  const isLocaleValid = (locales: {locale: string}[]) => {
    return locales.some((locale) => {
      return pathname.split("/")[0] === locale.locale;
    });
  };

  window.onload = function() {
    if(sessionStorage.getItem("endTime")){
      DataContainer.logEvent("session_end", "", {
        duration: parseInt(String((startTime - Number(sessionStorage.getItem(("endTime")))) / 1000)),
      });
    }
    sessionStorage.setItem("endTime", endTime.toString());
  };

  useEffect(() => {
    const appLoaded = sessionStorage.getItem("appLoaded");
    localStorage.setItem("segment", "regional_haul");

    if (!appLoaded) {
      if(currentPath.pathname.includes("configurator") || currentPath.pathname.includes("summary")) {
        DataContainer.sendDataToAdobeDataLayer(AdobeEventType.APPLICATION_INTERACTION, StepNumbers.STEP_0, StepNames.APPLICATION_LOADED, AdobeEventActions.APPLICATION_LOADED, SubStepNames.APPLICATION_LOADED, {"Segment": "No segment selected"}, "");
      }
      sessionStorage.setItem("appLoaded", "true");
    }
  }, []);

  useEffect(() => {
    DataContainer.logEvent("session_start", "", {
      start_time: startTime,
      source: DataContainer.getDeviceType(),
    });

    if (availableLocales.length === 0) {
      getLocales().then((res) => setAvailableLocales(res.data.data)).catch(() => setAvailableLocales([]));
    }
  } , []);

  useEffect(() => {
    if(availableLocales.length === 0) {
      return;
    }

    const localeFromLocation = location.href.split("/").find((v)=> v.length == 5 && v.includes("-"));
    const localeFromURL = Object.prototype.hasOwnProperty.call(params, "lang") ? params.lang.toLowerCase() : "";

    if (localeFromLocation && isLocaleExisting(availableLocales, localeFromLocation) && (pathname === "markets" || pathname === "")) {
      navigate({ pathname: `/${localeFromLocation}`, search: currentPath.search });
    } else if (!isLocaleValid(availableLocales) && !currentPath.pathname.includes("get_configuration")) {
      navigate("/markets");
    }

    availableLocales.forEach((locale: { locale: string; id: string }) => {
      if (currentPath.pathname.includes("get_configuration") && locale.locale === localeFromURL) {
        setSearchParams(params);
        const color = Object.prototype.hasOwnProperty.call(params, "Colors-Colors");
        color && setDefaultVariantColor(params["Colors-Colors"]);
        color && setIsColorComingFromURL(true);
        setCurrentLocale({locale: locale.locale, localeID: locale.id});
        navigate(
          `/${localeFromURL}/configurator/${params.product.toLowerCase()}`
        );
      } else if (pathname.split("/")[0] === locale.locale) {
        if(!currentLocale || currentLocale?.locale !== locale.locale) {
          setCurrentLocale({locale: locale.locale, localeID: locale.id});
        }
        navigate({ pathname: currentPath.pathname, search: currentPath.search });
      }
    });

    if (pathname.replaceAll("/", "") !== "ar-to-web" && pathname.replaceAll("/", "") !== localeFromURL){
      return;
    }

    if (currentPath.pathname === "/" && currentLocale?.locale && pathname !== "ar-to-web") {
      navigate("/markets");
    }
  }, [pathname, availableLocales, DataContainer.sectionKey(MarketSettings.MARKET_SETTINGS, MarketSettings.CONFIGURATOR_URL)]);

  return (
    <div className="flex min-h-screen flex-col justify-between flex-1" role="main">
      <Routes>
        <Route path="/markets" element={<Markets />} />
        <Route path="/:locale/configurator/:modelName/:componentFromUrl" element={<ConfiguratorRoute />} />
        <Route path="/:locale/configurator/:modelName" element={<ConfiguratorRoute />} />
        <Route path="/:locale/configurator" element={<ConfiguratorWithoutModelRedirect />} />
        <Route path="/:locale/summary/:modelNameFromUrl/:hash" element={<Summary />} />
        <Route path="/:locale/get_configuration/:hash" element={<ArToWeb />} />
        <Route path="/:locale/:powerCategory/:truckSlug" element={<TruckSelector />} />
        <Route path="/:locale/:powerCategory" element={<TruckSelector />} />
        <Route path="/:locale" element={<TruckSelector />} />
      </Routes>
    </div>
  );
}

export default App;
